import { z } from 'zod';

/**
 * Protections of the public opening (F16): what a person confirms before sending AI visitors to a site,
 * the domains whose owners opted out, and the limits against abuse of the Free plan.
 */

/** Version of the consent text: stored with every confirmation, so we know what was accepted. */
export const SITE_CONSENT_VERSION = '2026-10-20';

/** What the person confirms before AI visitors are sent to a site. */
export const SITE_CONSENT_TEXT = 'I own this site or have the permission of its owner to test it with AI visitors.';

export const siteConsentMessage = 'Confirm that you own the site or have permission to test it.';

/** Where a confirmation was given. */
export const CONSENT_SOURCES = ['analysis', 'project', 'test', 'saved_analysis'] as const;
export type ConsentSource = (typeof CONSENT_SOURCES)[number];

/** `https://www.Example.com/path` → `example.com`; a bare domain stays as it is. */
export function normalizeDomain(raw: string): string {
  let host = raw.trim().toLowerCase();
  try {
    if (/^[a-z][a-z0-9+.-]*:\/\//.test(host)) host = new URL(host).hostname;
  } catch {
    // keep it as typed
  }
  return host.replace(/\/.*$/, '').replace(/:\d+$/, '').replace(/\.$/, '').replace(/^www\./, '');
}

/** True when `host` is one of the excluded domains or a subdomain of one. */
export function isExcludedHost(host: string, excluded: readonly string[]): boolean {
  const h = normalizeDomain(host);
  return excluded.some((d) => {
    const domain = normalizeDomain(d);
    return domain !== '' && (h === domain || h.endsWith(`.${domain}`));
  });
}

const domainSchema = z
  .string()
  .trim()
  .min(1)
  .max(253)
  .transform(normalizeDomain)
  .refine((d) => /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d), { message: 'Use a domain like example.com' });

export const protectionSettingsSchema = z.object({
  /** Visits of Free organizations from one IP address in 30 days: limits several free accounts per person. */
  freeVisitsPerIpPerMonth: z.number().int().min(1).max(100_000),
  /** LLM spend of Free-plan tests per day (Europe/Rome), in USD as the costs are tracked. Beyond it they wait. */
  dailyBudgetUsd: z.number().min(0).max(10_000),
  /** Runners (browsers) running at the same time, free and paid tests together. */
  maxRunners: z.number().int().min(1).max(500),
  /** Sites whose owners asked not to be tested (subdomains included). */
  excludedDomains: z.array(domainSchema).max(5000),
});
export type ProtectionSettings = z.infer<typeof protectionSettingsSchema>;

export const DEFAULT_PROTECTION: ProtectionSettings = {
  freeVisitsPerIpPerMonth: 10,
  // About 15 €.
  dailyBudgetUsd: 17,
  maxRunners: 15,
  excludedDomains: [],
};

/** Why a test has not started yet: our runners are all busy, or today's budget of the Free plan is spent. */
/** `preparing`: the visitors' reasons are being written from the site's pages (a few seconds). */
export type AnalysisWaiting = 'preparing' | 'busy' | 'budget';

/** The ALTCHA challenge (proof of work) the public page solves before creating an analysis. */
export interface AltchaChallengeDto {
  parameters: {
    algorithm: string;
    nonce: string;
    salt: string;
    cost: number;
    keyLength: number;
    keyPrefix: string;
    keySignature?: string;
    expiresAt?: number;
    data?: Record<string, string | number | boolean | null>;
  };
  signature?: string;
}
